import React, { useState, useMemo } from "react";
import {
    Container,
    Grid,
    Card,
    CardContent,
    CardActions,
    Typography,
    Button,
    Box,
    Pagination,
    TextField,
    InputAdornment
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilePdf, faSearch } from "@fortawesome/free-solid-svg-icons";
import SectionTitle from "@others/title-section.layout";
import {tracts2026} from "../data";

const ITEMS_PER_PAGE = 9;

const PublishList2026Page: React.FC = () => {
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState("");

    // Recherche + pagination
    const filteredTracts = useMemo(() =>
            tracts2026.filter(
                (tract) =>
                    tract.title.toLowerCase().includes(search.toLowerCase()) ||
                    tract.description.toLowerCase().includes(search.toLowerCase()) ||
                    tract.date.toLowerCase().includes(search.toLowerCase())
            ),
        [search]
    );

    const paginatedTracts = useMemo(() => {
        const start = (page - 1) * ITEMS_PER_PAGE;
        return filteredTracts.slice(start, start + ITEMS_PER_PAGE);
    }, [filteredTracts, page]);

    // Remet à la page 1 en cas de nouvelle recherche
    React.useEffect(() => {
        setPage(1);
    }, [search]);

    return (
        <Container sx={{ mb: 5 }}>
            <SectionTitle title={"ESPACE CAMPAGNE VLD 2026"} />
            {/* Champ de recherche */}
            <Box mt={2} mb={3} display="flex" justifyContent="center">
                <TextField
                    variant="outlined"
                    size="small"
                    placeholder="Rechercher un tract..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <FontAwesomeIcon icon={faSearch} />
                            </InputAdornment>
                        ),
                        sx: { borderRadius: 3, bgcolor: "#f4f7fa" }
                    }}
                    sx={{ width: { xs: "100%", sm: "60%", md: "40%" } }}
                />
            </Box>

            <Box mt={4}>
                <Grid container spacing={4}>
                    {paginatedTracts.length > 0 ? paginatedTracts.map((tract) => (
                        <Grid size={{ xs: 12, md: 6, lg: 4 }} key={tract.id}>
                            <Card
                                sx={{
                                    height: "100%",
                                    display: "flex",
                                    flexDirection: "column",
                                    borderRadius: 3,
                                    boxShadow: 4,
                                    "&:hover": { boxShadow: 8 }
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        bgcolor: "#f4f7fa",
                                        py: 3,
                                        borderTopLeftRadius: 12,
                                        borderTopRightRadius: 12,
                                    }}
                                >
                                    <FontAwesomeIcon icon={faFilePdf} size="2x" style={{ color: "#d32f2f" }} />
                                </Box>
                                <CardContent>
                                    <Typography variant="h6" fontWeight={700} gutterBottom color="primary">
                                        {tract.title}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        {tract.date}
                                    </Typography>
                                    <Typography variant="body2" color="text.primary">
                                        {tract.description}
                                    </Typography>
                                </CardContent>
                                <CardActions sx={{ mt: "auto", p: 2, pt: 0 }}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        fullWidth
                                        href={tract.url}
                                        sx={{
                                            fontWeight: 600,
                                            textTransform: "none",
                                            borderRadius: 2
                                        }}
                                    >
                                        Lire le tract
                                    </Button>
                                </CardActions>
                            </Card>
                        </Grid>
                    )) : (
                        <Grid size={{ xs: 12 }}>
                            <Box textAlign="center" py={5} color="text.secondary">
                                <Typography variant="h6" gutterBottom>
                                    Aucun tract trouvé.
                                </Typography>
                                <Typography variant="body2">
                                    Essayez un autre mot-clé.
                                </Typography>
                            </Box>
                        </Grid>
                    )}
                </Grid>

                {/* Pagination */}
                {filteredTracts.length > ITEMS_PER_PAGE && (
                    <Box display="flex" justifyContent="center" mt={4}>
                        <Pagination
                            count={Math.ceil(filteredTracts.length / ITEMS_PER_PAGE)}
                            page={page}
                            onChange={(_, value) => setPage(value)}
                            shape="rounded"
                            color="primary"
                        />
                    </Box>
                )}
            </Box>
        </Container>
    );
};

export default PublishList2026Page;